import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PDFDocument } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import {
  FaFilePdf,
  FaTrash,
  FaUndo,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
} from 'react-icons/fa';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const PREVIEW_SCALE = 1.5;
const MIN_BOX = 0.005; // ignore accidental clicks (fraction of page)

const QUALITY_OPTIONS = [
  { label: 'Standard (~110 dpi)', value: 1.5 },
  { label: 'High (~150 dpi)', value: 2 },
  { label: 'Maximum (~220 dpi)', value: 3 },
];

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

function PdfRedactor() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  // { [pageNumber]: [{ id, x, y, w, h }] } — all values are fractions (0–1) of the page
  const [redactions, setRedactions] = useState({});
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [quality, setQuality] = useState(2);
  const [error, setError] = useState('');
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const drawStart = useRef(null);
  const renderTaskRef = useRef(null);
  const nextId = useRef(1);

  // ---------- Loading a file ----------
  const loadFile = useCallback(async (f) => {
    setError('');
    setLoading(true);
    try {
      const buffer = await f.arrayBuffer();
      const doc = await pdfjsLib.getDocument({ data: new Uint8Array(buffer) })
        .promise;
      setPdfDoc((prev) => {
        prev?.destroy();
        return doc;
      });
      setFile(f);
      setNumPages(doc.numPages);
      setCurrentPage(1);
      setRedactions({});
      setDraft(null);
    } catch (err) {
      console.error('Failed to open PDF:', err);
      setError(
        err?.name === 'PasswordException'
          ? 'This PDF is password protected. Remove the password and try again.'
          : 'Could not open that PDF. It may be damaged or unsupported.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFiles = useCallback(
    (list) => {
      const pdf = Array.from(list).find((f) => f.type === 'application/pdf');
      if (pdf) loadFile(pdf);
    },
    [loadFile]
  );

  const handleFileChange = (e) => {
    handleFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDraggingOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDropZoneDragOver = (e) => {
    e.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDropZoneDragLeave = () => {
    setIsDraggingOver(false);
  };

  const closeFile = () => {
    pdfDoc?.destroy();
    setPdfDoc(null);
    setFile(null);
    setNumPages(0);
    setCurrentPage(1);
    setRedactions({});
    setDraft(null);
    setError('');
  };

  // ---------- Rendering the current page ----------
  useEffect(() => {
    if (!pdfDoc) return;
    let cancelled = false;

    (async () => {
      try {
        const page = await pdfDoc.getPage(currentPage);
        if (cancelled) return;
        const viewport = page.getViewport({ scale: PREVIEW_SCALE });
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        renderTaskRef.current?.cancel();
        const task = page.render({ canvasContext: ctx, viewport });
        renderTaskRef.current = task;
        await task.promise;
      } catch (err) {
        if (err?.name !== 'RenderingCancelledException') {
          console.error('Failed to render page:', err);
        }
      }
    })();

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
    };
  }, [pdfDoc, currentPage]);

  // ---------- Drawing redaction boxes ----------
  const getPoint = (e) => {
    const rect = overlayRef.current.getBoundingClientRect();
    return {
      x: clamp((e.clientX - rect.left) / rect.width, 0, 1),
      y: clamp((e.clientY - rect.top) / rect.height, 0, 1),
    };
  };

  const handlePointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = getPoint(e);
    drawStart.current = p;
    setDraft({ x: p.x, y: p.y, w: 0, h: 0 });
  };

  const handlePointerMove = (e) => {
    if (!drawStart.current) return;
    const p = getPoint(e);
    const s = drawStart.current;
    setDraft({
      x: Math.min(s.x, p.x),
      y: Math.min(s.y, p.y),
      w: Math.abs(p.x - s.x),
      h: Math.abs(p.y - s.y),
    });
  };

  const handlePointerUp = () => {
    if (!drawStart.current) return;
    drawStart.current = null;
    if (draft && draft.w > MIN_BOX && draft.h > MIN_BOX) {
      const box = { ...draft, id: nextId.current++ };
      setRedactions((prev) => ({
        ...prev,
        [currentPage]: [...(prev[currentPage] || []), box],
      }));
    }
    setDraft(null);
  };

  const removeBox = (id) => {
    setRedactions((prev) => ({
      ...prev,
      [currentPage]: (prev[currentPage] || []).filter((b) => b.id !== id),
    }));
  };

  const undoLast = () => {
    setRedactions((prev) => ({
      ...prev,
      [currentPage]: (prev[currentPage] || []).slice(0, -1),
    }));
  };

  const clearPage = () => {
    setRedactions((prev) => ({ ...prev, [currentPage]: [] }));
  };

  const goToPage = (n) => setCurrentPage(clamp(n, 1, numPages));

  // ---------- Export ----------
  const redactAndDownload = async () => {
    if (!pdfDoc || !file) return;
    setExporting(true);
    setProgress(0);
    setError('');

    try {
      const out = await PDFDocument.create();
      let original = null;

      for (let p = 1; p <= numPages; p++) {
        const boxes = redactions[p] || [];

        if (boxes.length === 0) {
          // Untouched page: copy as-is so text stays selectable and file stays small
          if (!original) {
            original = await PDFDocument.load(await file.arrayBuffer());
          }
          const [copied] = await out.copyPages(original, [p - 1]);
          out.addPage(copied);
        } else {
          // Redacted page: flatten to an image with black boxes burned in,
          // so the covered text/graphics no longer exist in the file.
          const page = await pdfDoc.getPage(p);
          const base = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: quality });

          const canvas = document.createElement('canvas');
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#fff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          await page.render({ canvasContext: ctx, viewport }).promise;

          ctx.fillStyle = '#000';
          boxes.forEach((b) => {
            ctx.fillRect(
              Math.floor(b.x * canvas.width),
              Math.floor(b.y * canvas.height),
              Math.ceil(b.w * canvas.width),
              Math.ceil(b.h * canvas.height)
            );
          });

          const blob = await new Promise((resolve) =>
            canvas.toBlob(resolve, 'image/jpeg', 0.92)
          );
          const image = await out.embedJpg(await blob.arrayBuffer());
          const newPage = out.addPage([base.width, base.height]);
          newPage.drawImage(image, {
            x: 0,
            y: 0,
            width: base.width,
            height: base.height,
          });

          canvas.width = canvas.height = 0;
          page.cleanup();
        }
        setProgress(p);
      }

      const bytes = await out.save();
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${file.name.replace(/\.pdf$/i, '')}-redacted.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to redact PDF:', err);
      setError('Something went wrong while redacting. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const pageBoxes = redactions[currentPage] || [];
  const totalBoxes = Object.values(redactions).reduce(
    (sum, list) => sum + list.length,
    0
  );
  const redactedPageCount = Object.values(redactions).filter(
    (list) => list.length > 0
  ).length;

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-3xl md:text-4xl text-primary">
                PDF Redactor
              </CardTitle>
              <button
                onClick={() => navigate('/')}
                className="text-sm text-primary bg-primary/15 px-4 py-1 rounded-full"
              >
                Back Home
              </button>
            </div>
            <p className="text-muted-foreground mt-2">
              Black out sensitive areas of a PDF. Everything happens in your
              browser — nothing is uploaded.
            </p>
          </CardHeader>
        </Card>

        {/* Drop zone */}
        {!file && (
          <Card>
            <CardContent className="p-6">
              <div
                onDrop={handleDrop}
                onDragOver={handleDropZoneDragOver}
                onDragLeave={handleDropZoneDragLeave}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
                  isDraggingOver
                    ? 'border-primary bg-primary/10'
                    : 'border-muted-foreground/30 hover:border-primary/50'
                }`}
              >
                <FaFilePdf className="text-5xl text-primary mx-auto mb-4" />
                <p className="text-lg font-medium">
                  {loading
                    ? 'Opening PDF...'
                    : 'Drop a PDF here or click to browse'}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Only .pdf files are accepted
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
              {error && (
                <p className="text-sm text-destructive mt-4">{error}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Editor */}
        {file && (
          <>
            {/* File info */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <FaFilePdf className="text-primary text-lg shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-medium">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatSize(file.size)} · {numPages}{' '}
                      {numPages === 1 ? 'page' : 'pages'}
                    </p>
                  </div>
                  <button
                    onClick={closeFile}
                    disabled={exporting}
                    className="text-sm text-primary bg-primary/15 px-4 py-1 rounded-full disabled:opacity-50"
                  >
                    Choose another
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Page viewer */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-xl">
                    Page {currentPage} of {numPages}
                  </CardTitle>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-1.5 rounded hover:bg-accent disabled:opacity-30 transition-colors"
                      aria-label="Previous page"
                    >
                      <FaChevronLeft className="text-sm" />
                    </button>
                    <button
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === numPages}
                      className="p-1.5 rounded hover:bg-accent disabled:opacity-30 transition-colors"
                      aria-label="Next page"
                    >
                      <FaChevronRight className="text-sm" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Click and drag over anything you want to black out.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg border border-border overflow-hidden bg-muted/30">
                  <div className="relative mx-auto select-none">
                    <canvas ref={canvasRef} className="block w-full h-auto" />
                    <div
                      ref={overlayRef}
                      onPointerDown={handlePointerDown}
                      onPointerMove={handlePointerMove}
                      onPointerUp={handlePointerUp}
                      onPointerCancel={handlePointerUp}
                      className="absolute inset-0 cursor-crosshair"
                      style={{ touchAction: 'none' }}
                    >
                      {pageBoxes.map((b) => (
                        <div
                          key={b.id}
                          className="absolute bg-black"
                          style={{
                            left: `${b.x * 100}%`,
                            top: `${b.y * 100}%`,
                            width: `${b.w * 100}%`,
                            height: `${b.h * 100}%`,
                          }}
                        >
                          <button
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={() => removeBox(b.id)}
                            className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-destructive text-white flex items-center justify-center shadow"
                            aria-label="Remove redaction"
                          >
                            <FaTimes className="text-[10px]" />
                          </button>
                        </div>
                      ))}
                      {draft && (
                        <div
                          className="absolute border-2 border-primary bg-primary/30"
                          style={{
                            left: `${draft.x * 100}%`,
                            top: `${draft.y * 100}%`,
                            width: `${draft.w * 100}%`,
                            height: `${draft.h * 100}%`,
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm text-muted-foreground">
                    {pageBoxes.length}{' '}
                    {pageBoxes.length === 1 ? 'box' : 'boxes'} on this page
                  </p>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={undoLast}
                      disabled={pageBoxes.length === 0}
                      className="flex items-center gap-2 px-3 py-1.5 rounded text-sm hover:bg-accent disabled:opacity-30 transition-colors"
                    >
                      <FaUndo className="text-xs" />
                      Undo
                    </button>
                    <button
                      onClick={clearPage}
                      disabled={pageBoxes.length === 0}
                      className="flex items-center gap-2 px-3 py-1.5 rounded text-sm text-destructive hover:bg-destructive/20 disabled:opacity-30 transition-colors"
                    >
                      <FaTrash className="text-xs" />
                      Clear page
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Export */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Download</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label
                    htmlFor="redact-quality"
                    className="text-sm font-medium"
                  >
                    Quality of redacted pages
                  </label>
                  <select
                    id="redact-quality"
                    value={quality}
                    onChange={(e) => setQuality(Number(e.target.value))}
                    disabled={exporting}
                    className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  >
                    {QUALITY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <p className="text-xs text-muted-foreground">
                  Redacted pages are converted to images so the hidden content
                  is permanently removed, which means their text can no longer
                  be selected or searched. Pages without redactions are left
                  untouched.
                </p>

                {error && <p className="text-sm text-destructive">{error}</p>}

                <button
                  onClick={redactAndDownload}
                  disabled={totalBoxes === 0 || exporting}
                  className="w-full py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {exporting
                    ? `Redacting... (${progress}/${numPages})`
                    : totalBoxes === 0
                      ? 'Draw at least one box to redact'
                      : `Redact ${totalBoxes} ${
                          totalBoxes === 1 ? 'area' : 'areas'
                        } on ${redactedPageCount} ${
                          redactedPageCount === 1 ? 'page' : 'pages'
                        }`}
                </button>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

export default PdfRedactor;
